import { createEffect, onMount } from 'solid-js'
import { PerspectiveCamera, Vector3, type Camera } from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { useDepthTextureSignal } from '../../../rx/signal/useDepthTextureSignal'
import type { Engine } from '../../core/engine/Engine.type'
import { getEngineThreeControls } from '../fn/getSetEngineThreeControls'
import createDepthColorEffectShader from './createDepthColorEffectShader'

export default function createMountedDepthColorEffectShader(
  engine: Engine,
  composer: EffectComposer,
  camera: Camera,
  // renderTarget: WebGLRenderTarget,
) {
  const [getDepthTexture] = useDepthTextureSignal(engine)
  const worldDown = new Vector3(0, -1, 0)
  const cameraForward = new Vector3()

  const depthColorEffect = new ShaderPass(createDepthColorEffectShader())
  // You MUST provide the depth texture to this shader
  // The correct depth texture is usually from the composer's read buffer
  // or the main render target if this is the first pass using depth.
  // This needs to be updated each frame if the readBuffer changes.

  // Set camera uniforms (can be done once if camera doesn't change near/far planes)
  if (camera instanceof PerspectiveCamera) {
    depthColorEffect.uniforms.cameraNear.value = camera.near
    depthColorEffect.uniforms.cameraFar.value = camera.far
  }

  const updateCameraPitchUniform = () => {
    const controls = getEngineThreeControls(engine)
    if (controls && 'getPolarAngle' in controls && typeof controls.getPolarAngle === 'function') {
      depthColorEffect.uniforms.cameraPitchFromTopDownDeg.value = controls.getPolarAngle() * (180 / Math.PI)
      return
    }

    camera.getWorldDirection(cameraForward)
    const dot = Math.max(-1, Math.min(1, cameraForward.dot(worldDown)))
    depthColorEffect.uniforms.cameraPitchFromTopDownDeg.value = Math.acos(dot) * (180 / Math.PI)
  }

  updateCameraPitchUniform()

  const originalRender = depthColorEffect.render.bind(depthColorEffect)
  depthColorEffect.render = (renderer, writeBuffer, readBuffer, deltaTime, maskActive) => {
    updateCameraPitchUniform()
    originalRender(renderer, writeBuffer, readBuffer, deltaTime, maskActive)
  }

  composer.addPass(depthColorEffect)

  // createMountedInverval(() => {
  //   depthColorEffect.enabled = !depthColorEffect.enabled
  //   console.log('[DepthColorEffect]', 'enabled', depthColorEffect.enabled)
  //   // depthColorEffect.enabled = false
  // }, 3000)

  // depthColorEffect.enabled = true
  // depthColorEffect.enabled = false

  onMount(() => {
    const last = composer.passes.at(-1)
    if (last) {
      last.renderToScreen = true
    }
  })
  // createEffect(() => {
  //   const depthTexture = getDepthTexture()
  //   console.log('getDepthTexture.effect', depthTexture)
  // })
  createEffect(() => {
    const depthTexture = getDepthTexture()
    // depthColorEffect.enabled = !!depthTexture
    if (depthTexture) {
      depthColorEffect.uniforms.tDepth.value = depthTexture
    }
  })

  return depthColorEffect
}
